import React from 'react';
import { Card, Typography, Row, Col, Tag, Spin, Avatar } from 'antd';
import { IoPersonOutline } from 'react-icons/io5';
import { MdShield, MdStar } from 'react-icons/md';
import { useGetCurrentUser } from '@/hooks/app';

const { Title, Text } = Typography;

export const CurrentUserCard: React.FC = () => {
  const { data: currentUser, isLoading } = useGetCurrentUser();

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center items-center py-8">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!currentUser?.currentUser) {
    return (
      <Card>
        <Text type="secondary">No user data available</Text>
      </Card>
    );
  }

  const user = currentUser.currentUser;
  const defaultRole = currentUser.defaultRole;
  const customRole = currentUser.customRole;

  return (
    <Card className="shadow-sm">
      <div className="flex items-center gap-4 mb-4 pb-4 border-b-2 border-[#FEEAE9]">
        <Avatar size={64} icon={<IoPersonOutline />} className="bg-[#DC3B41]" />
        <div>
          <Title level={4} className="mb-1">
            {user.firstname} {user.lastname}
          </Title>
          <Text type="secondary" className="text-sm">{user.internalemailaddress || user.domainname}</Text>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Text type="secondary" className="block text-sm mb-1">Full Name</Text>
          <Text strong className="block">
            {user.fullname || `${user.firstname} ${user.lastname}`}
          </Text>
        </Col>

        <Col xs={24} md={12}>
          <Text type="secondary" className="block text-sm mb-1">Email / Domain</Text>
          <Text className="block">{user.internalemailaddress || user.domainname}</Text>
        </Col>



        {user.dogma_team && (
          <Col xs={24} md={12}>
            <Text type="secondary" className="block text-sm mb-1">Team</Text>
            <Tag color="blue">
              {user['dogma_team@OData.Community.Display.V1.FormattedValue'] || `Team ${user.dogma_team}`}
            </Tag>
          </Col>
        )}

        {user.dogma_region && (
          <Col xs={24} md={12}>
            <Text type="secondary" className="block text-sm mb-1">Region</Text>
            <Tag color="purple">
              {user['dogma_region@OData.Community.Display.V1.FormattedValue'] || `Region ${user.dogma_region}`}
            </Tag>
          </Col>
        )}

        <Col xs={24}>
          <div className="mt-4 pt-4 border-t border-[#FEEAE9]">
            <Text strong className="block text-sm mb-3">Assigned Roles</Text>
            <div className="flex flex-col gap-3">
              {defaultRole && (
                <div className="rounded-lg p-4 border border-[#FEEAE9] bg-gradient-to-r from-[#FEEAE9] to-[#FEF5F4] hover:shadow-sm transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#DC3B41]">
                      <MdShield className="text-white" size={20} />
                    </div>
                    <div className="flex-1">
                      <Text strong className="block text-[#DC3B41]">
                        {defaultRole.dogma_name}
                      </Text>
                      <Text type="secondary" className="text-xs">
                        System security role with base permissions
                      </Text>
                    </div>
                    <Tag className="bg-[#FEEAE9] text-[#DC3B41] border-none">
                      Default
                    </Tag>
                  </div>
                </div>
              )}

              {customRole && (
                <div className="rounded-lg p-4 border border-[#FEEAE9] bg-gradient-to-r from-[#FEEAE9] to-[#FEF5F4] hover:shadow-sm transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#DC3B41]">
                      <MdStar className="text-white" size={20} />
                    </div>
                    <div className="flex-1">
                      <Text strong className="block text-[#DC3B41]">
                        {customRole.dogma_name}
                      </Text>
                      <Text type="secondary" className="text-xs">
                        Additional permissions and custom access
                      </Text>
                    </div>
                    <Tag className="bg-[#FEEAE9] text-[#DC3B41] border-none">
                      Custom
                    </Tag>
                  </div>
                </div>
              )}

              {!defaultRole && !customRole && (
                <div className="text-center py-4 text-gray-400">
                  <Text type="secondary">No roles assigned</Text>
                </div>
              )}
            </div>
          </div>
        </Col>

        {user.createdon && (
          <Col xs={24} md={12}>
            <Text type="secondary" className="block text-sm mb-1">Created On</Text>
            <Text className="block text-xs">
              {user['createdon@OData.Community.Display.V1.FormattedValue'] ||
                new Date(user.createdon).toLocaleDateString()}
            </Text>
          </Col>
        )}

        {user.modifiedon && (
          <Col xs={24} md={12}>
            <Text type="secondary" className="block text-sm mb-1">Last Modified</Text>
            <Text className="block text-xs">
              {user['modifiedon@OData.Community.Display.V1.FormattedValue'] ||
                new Date(user.modifiedon).toLocaleDateString()}
            </Text>
          </Col>
        )}
      </Row>
    </Card>
  );
};
